package com.exura.pages.register

import androidx.compose.runtime.*
import com.exura.components.buttons.AsyncButton
import com.exura.components.inputs.InlineTextInput
import com.exura.components.inputs.InlineTextInputWithError
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraBodySizeTextVariant
import com.exura.components.pageTypography.ExuraEmphasisedTextVariant
import com.exura.components.pageTypography.ExuraErrorTextVariant
import com.exura.components.pageTypography.ExuraLinkTextVariant
import com.exura.components.pageTypography.ExuraPageHeaderTextVariant
import com.exura.components.pageTypography.ExuraPoppinsVariant
import com.exura.components.pageTypography.ExuraSectionHeaderTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralCardEntry
import com.exura.components.pageTypography.InteractiveCardEntryVariant
import com.exura.features.newauth.domain.model.ProviderUserRole
import com.exura.navigation.Route
import com.exura.primary
import com.exura.transparent
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.times

@Page(Route.REGISTER)
@Layout(".components.layouts.NotLoggedInLayout")
@Composable
internal fun RegisterPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val controller = rememberRegisterController(
        context = context,
    )
    val state = controller.uiState

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(Dimens.loggingBoxMinHeight),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap)
    ) {
        if (state.showSuccessMessage) {
            SuccessMainMessage()
            Spacer()
            ReturnToLoginButton(
                onClick = suspend {
                    delay(300)
                    controller.handleEvent(RegisterEvent.NavigateToLogin)
                }
            )
        } else {
            NewAccountHeader()

            if (state.registrationError != null) {
                ErrorBox(state.registrationError)
            }

            EmailField(
                email = state.email,
                emailError = state.emailError,
                onEmailChange = { scope.launch { controller.handleEvent(RegisterEvent.EmailChanged(it)) } },
                enabled = !state.isRegistering
            )

            RoleSelector(
                selectedRole = state.selectedRole,
                onRoleSelected = { scope.launch { controller.handleEvent(RegisterEvent.RoleSelected(it)) } }
            )

            if (state.selectedRole == ProviderUserRole.DOCTOR) {
                DoctorForm(
                    fullName = state.fullName,
                    specialization = state.specialization,
                    onFullNameChange = { scope.launch { controller.handleEvent(RegisterEvent.FullNameChanged(it)) } },
                    onSpecializationChange = { scope.launch { controller.handleEvent(RegisterEvent.SpecializationChanged(it)) } },
                    isRegistering = state.isRegistering
                )
            } else {
                OrganizationForm(
                    organizationName = state.organizationName,
                    fieldOfActivity = state.field,
                    phoneNumber = state.contactNumber,
                    onOrganizationNameChange = { scope.launch { controller.handleEvent(RegisterEvent.OrganizationNameChanged(it)) } },
                    onFieldChange = { scope.launch { controller.handleEvent(RegisterEvent.FieldChanged(it)) } },
                    onContactNumberChange = { scope.launch { controller.handleEvent(RegisterEvent.ContactNumberChanged(it)) } },
                    isRegistering = state.isRegistering,
                )
            }

            RegisterButton(
                onClick = suspend { controller.handleEvent(RegisterEvent.RegisterClicked) },
                enabled = !state.isRegistering
            )

            Spacer()

            BackToLogin(onClick = { scope.launch { controller.handleEvent(RegisterEvent.NavigateToLogin) } })
        }
    }
}

@Composable
private fun RoleSelector(
    selectedRole: ProviderUserRole,
    onRoleSelected: (ProviderUserRole) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap / 2)
    ) {
        SpanText(
            modifier = ExuraTextStyle
                .toModifier(ExuraSectionHeaderTextVariant),
            text = registerLocalization.current.accountTypePrompt
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap / 2)
        ) {
            RoleSelectionBox(
                text = registerLocalization.current.accountTypes.doctor,
                isSelected = selectedRole == ProviderUserRole.DOCTOR,
                onClick = { onRoleSelected(ProviderUserRole.DOCTOR) },
                modifier = Modifier
                    .weight(1f)
            )

            RoleSelectionBox(
                text = registerLocalization.current.accountTypes.organization,
                isSelected = selectedRole == ProviderUserRole.ORGANIZATION,
                onClick = { onRoleSelected(ProviderUserRole.ORGANIZATION) },
                modifier = Modifier
                    .weight(1f)
            )
        }
    }
}

@Composable
private fun RoleSelectionBox(
    text: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val colorMode = ColorMode.current
    val palette = colorMode.toPalette()
    GeneralCardEntry(
        modifier = Modifier
            .backgroundColor(
                if (isSelected) {
                    if (colorMode.isLight) {
                        palette.primary.lightened(0.75f)
                    } else
                        palette.primary.darkened(0.75f)
                } else palette.transparent
            )
            .border {
                width(Dimens.cardEntryBorderWidth)
                style(LineStyle.Solid)
                color(if (isSelected) palette.primary else palette.border)
            }
            .onClick { onClick() }
            .cursor(Cursor.Pointer)
            .then(modifier),
        variant = InteractiveCardEntryVariant,
    ) {
        SpanText(text)
    }
}

@Composable
private fun DoctorForm(
    fullName: String,
    specialization: String,
    onFullNameChange: (String) -> Unit,
    onSpecializationChange: (String) -> Unit,
    isRegistering: Boolean
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap),
    ) {
        InlineTextInput(
            value = fullName,
            onValueChange = onFullNameChange,
            isEnabled = !isRegistering,
            placeholderText = registerLocalization.current.fields.fullName,
        )

        InlineTextInput(
            value = specialization,
            onValueChange = onSpecializationChange,
            isEnabled = !isRegistering,
            placeholderText = registerLocalization.current.fields.specialization,
        )
    }
}

@Composable
private fun OrganizationForm(
    organizationName: String,
    fieldOfActivity: String,
    phoneNumber: String,
    onOrganizationNameChange: (String) -> Unit,
    onFieldChange: (String) -> Unit,
    onContactNumberChange: (String) -> Unit,
    isRegistering: Boolean
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap),
    ) {
        InlineTextInput(
            value = organizationName,
            onValueChange = onOrganizationNameChange,
            isEnabled = !isRegistering,
            placeholderText = registerLocalization.current.fields.fullName,
            modifier = Modifier.fillMaxWidth(),
        )

        InlineTextInput(
            value = fieldOfActivity,
            onValueChange = onFieldChange,
            isEnabled = !isRegistering,
            placeholderText = registerLocalization.current.fields.fieldOfActivity,
            modifier = Modifier.fillMaxWidth(),
        )

        InlineTextInput(
            value = phoneNumber,
            onValueChange = onContactNumberChange,
            isEnabled = !isRegistering,
            placeholderText = registerLocalization.current.fields.contactPhoneNumber,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun EmailField(
    email: String,
    emailError: String,
    onEmailChange: (String) -> Unit,
    enabled: Boolean
) {
    InlineTextInputWithError(
        value = email,
        error = emailError,
        onValueChange = onEmailChange,
        isEnabled = enabled,
        placeholderText = registerLocalization.current.fields.emailAddress,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun ErrorBox(errorMessage: String) {
    SpanText(
        modifier = ExuraTextStyle.toModifier(ExuraErrorTextVariant).fillMaxWidth(),
        text = errorMessage,
    )
}

@Composable
private fun NewAccountHeader() {
    Box(
        modifier = ExuraTextStyle
            .toModifier(ExuraPageHeaderTextVariant, ExuraEmphasisedTextVariant)
            .fillMaxWidth()
            .textAlign(TextAlign.Center),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(registerLocalization.current.pageTitle)
    }
}

@Composable
private fun RegisterButton(
    onClick: suspend () -> Unit,
    enabled: Boolean,
) {
    AsyncButton(
        text = registerLocalization.current.actions.register,
        onClick = onClick,
        isDisabled = !enabled,
        modifier = Modifier
            .fillMaxWidth()
            .fontSize(Dimens.loggingBoxInputsFontSize)
            .height(1.75 * Dimens.buttonHeight)
    )
}

@Composable
private fun BackToLogin(
    onClick: () -> Unit,
) {
    Row(
        modifier = ExuraTextStyle
            .toModifier(ExuraPoppinsVariant, ExuraBodySizeTextVariant),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.inlineTextRelGapSize),
    ) {
        SpanText(
            text = registerLocalization.current.messages.alreadyHaveAccount
        )

        SpanText(
            modifier = ExuraTextStyle
                .toModifier(ExuraPoppinsVariant, ExuraLinkTextVariant)
                .onClick { onClick() },
            text = registerLocalization.current.actions.login
        )
    }
}

@Composable
private fun SuccessMainMessage() {
    SpanText(
        modifier = ExuraTextStyle
            .toModifier(ExuraPageHeaderTextVariant, ExuraEmphasisedTextVariant),
        text = registerLocalization.current.messages.registrationSuccessful
    )

    SpanText(
        modifier = ExuraTextStyle
            .toModifier(ExuraBodyHeaderTextVariant),
        text = registerLocalization.current.messages.thankYouForRegistering
    )

    Spacer()

    SpanText(
        modifier = ExuraTextStyle
            .toModifier(ExuraBodySizeTextVariant),
        text = registerLocalization.current.messages.accountPendingApproval
    )
}

@Composable
private fun ReturnToLoginButton(
    onClick: suspend () -> Unit,
) {
    AsyncButton(
        text = registerLocalization.current.actions.returnToLogin,
        onClick = onClick,
        modifier = Modifier.fillMaxWidth()
            .fontSize(Dimens.loggingBoxInputsFontSize)
            .height(1.75 * Dimens.buttonHeight),
    )
}
